import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { logout } from "../../LoginPage/redux/auth.thunks"
import type { ReportDefinitionV1 } from "../types/canvas.types.ts"
import { reportCanvasSliceName } from "./reportCanvas.constants.ts"
import {
  fetchReportCatalog,
  resolveReportWidget,
  saveReportCanvas
} from "./reportCanvas.thunks.ts"
import type { ReportCanvasState } from "./reportCanvas.types.ts"

const initialState: ReportCanvasState = {
  reportId: null,
  definition: null,
  catalog: null,
  catalogStatus: "idle",
  resolved: {},
  pendingResolveIds: {},
  dirtySeq: 0,
  savedSeq: 0,
  saveStatus: "idle",
  saveError: null,
  currentSaveRequestId: undefined,
  refreshSeq: 0,
  libraryOpen: false,
  configWidgetId: null
}

const reportCanvasSlice = createSlice({
  name: reportCanvasSliceName,
  initialState,
  reducers: {
    reportCanvasHydrate: (
      state,
      action: PayloadAction<{
        reportId: string
        definition: ReportDefinitionV1
      }>
    ) => {
      state.reportId = action.payload.reportId
      state.definition = action.payload.definition
      state.resolved = {}
      state.pendingResolveIds = {}
      state.dirtySeq = 0
      state.savedSeq = 0
      state.saveStatus = "idle"
      state.saveError = null
      state.currentSaveRequestId = undefined
    },
    reportCanvasApplyDefinition: (
      state,
      action: PayloadAction<ReportDefinitionV1>
    ) => {
      state.definition = action.payload
      state.dirtySeq += 1
      if (state.saveStatus !== "saving") state.saveStatus = "dirty"
    },
    reportCanvasSetLibraryOpen: (state, action: PayloadAction<boolean>) => {
      state.libraryOpen = action.payload
    },
    reportCanvasSetConfigWidgetId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.configWidgetId = action.payload
    },
    reportCanvasRequestRefresh: (state) => {
      state.refreshSeq += 1
    },
    reportCanvasReset: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReportCatalog.pending, (state) => {
        state.catalogStatus = "loading"
      })
      .addCase(fetchReportCatalog.fulfilled, (state, action) => {
        state.catalog = action.payload
        state.catalogStatus = "ready"
      })
      .addCase(fetchReportCatalog.rejected, (state) => {
        state.catalogStatus = "error"
      })
      .addCase(resolveReportWidget.pending, (state, action) => {
        const { widgetId } = action.meta.arg
        state.pendingResolveIds[widgetId] = action.meta.requestId
        if (!state.resolved[widgetId]) {
          state.resolved[widgetId] = { status: "loading", resolved: null }
        }
      })
      .addCase(resolveReportWidget.fulfilled, (state, action) => {
        const { widgetId } = action.meta.arg
        if (state.pendingResolveIds[widgetId] !== action.meta.requestId) return
        delete state.pendingResolveIds[widgetId]
        if ("failed" in action.payload) {
          state.resolved[widgetId] = { status: "error", resolved: null }
          return
        }
        state.resolved[widgetId] = {
          status: "ready",
          resolved: action.payload.resolved
        }
      })
      .addCase(resolveReportWidget.rejected, (state, action) => {
        const { widgetId } = action.meta.arg
        if (state.pendingResolveIds[widgetId] !== action.meta.requestId) return
        delete state.pendingResolveIds[widgetId]
        state.resolved[widgetId] = { status: "error", resolved: null }
      })
      .addCase(saveReportCanvas.pending, (state, action) => {
        state.saveStatus = "saving"
        state.saveError = null
        state.currentSaveRequestId = action.meta.requestId
      })
      .addCase(saveReportCanvas.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentSaveRequestId) return
        state.currentSaveRequestId = undefined
        state.savedSeq = action.payload.dirtySeq
        state.saveStatus = state.dirtySeq === state.savedSeq ? "saved" : "dirty"
      })
      .addCase(saveReportCanvas.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentSaveRequestId) return
        state.currentSaveRequestId = undefined
        state.saveStatus = "error"
        state.saveError =
          (action.payload as { message?: string } | undefined)?.message ??
          "Failed to save report."
      })
      .addCase(logout.fulfilled, () => initialState)
  }
})

export const {
  reportCanvasHydrate,
  reportCanvasApplyDefinition,
  reportCanvasSetLibraryOpen,
  reportCanvasSetConfigWidgetId,
  reportCanvasRequestRefresh,
  reportCanvasReset
} = reportCanvasSlice.actions

export default reportCanvasSlice
